import { differenceInCalendarDays, parseISO } from 'date-fns'
import { fees, getProperty } from '../data/units'
import { BookingType, PriceEstimate, PriceLine, PropertyId } from '../types/booking'
import { formatNaira, pluralize } from './format'

interface EstimateInput {
  bookingType: BookingType
  propertyId: PropertyId
  checkIn: string | null
  checkOut: string | null
  earlyCheckIn: boolean
  lateCheckout: boolean
}

export function nightsBetween(checkIn: string | null, checkOut: string | null): number {
  if (!checkIn || !checkOut) return 0
  const nights = differenceInCalendarDays(parseISO(checkOut), parseISO(checkIn))
  return nights > 0 ? nights : 0
}

const earlyLateRange = `${formatNaira(fees.earlyLateMin)}–${formatNaira(fees.earlyLateMax)}`

/**
 * Display-only estimate. The Apps Script backend is authoritative for every amount —
 * nothing calculated here is sent to it.
 */
export function estimatePrice(input: EstimateInput): PriceEstimate {
  const lines: PriceLine[] = []

  if (input.bookingType === 'photoshoot') {
    lines.push({
      label: `Photoshoot session (${fees.photoshootHours} hours)`,
      amount: fees.photoshootSession,
    })
    lines.push({ label: 'Refundable caution fee', amount: fees.cautionFee })
    return { lines, nights: 0, total: fees.photoshootSession + fees.cautionFee }
  }

  const property = getProperty(input.propertyId)
  const nights = nightsBetween(input.checkIn, input.checkOut)
  const base = property.nightlyRate * nights

  if (nights > 0) {
    lines.push({
      label: `${formatNaira(property.nightlyRate)} × ${pluralize(nights, 'night')}`,
      detail: property.name,
      amount: base,
    })
  }
  if (input.earlyCheckIn) {
    lines.push({ label: 'Early check-in', amount: 0, conditional: `${earlyLateRange} if approved` })
  }
  if (input.lateCheckout) {
    lines.push({ label: 'Late checkout', amount: 0, conditional: `${earlyLateRange} if approved` })
  }
  lines.push({ label: 'Refundable caution fee', amount: fees.cautionFee })

  return { lines, nights, total: nights > 0 ? base + fees.cautionFee : 0 }
}
